/**
 * LS Key Features — hotspot pop-ups.
 *
 * One pop-up open at a time. Escape closes it and hands focus back to the
 * marker that opened it. With JS off every pop-up stays hidden and the feature
 * list in the text column carries the same content.
 */
class LSKeyFeatures {
  constructor(section) {
    this.section = section;
    this.media = section.querySelector('[data-lskf-media]');
    this.hotspots = Array.from(section.querySelectorAll('[data-lskf-hotspot]'));
    if (this.hotspots.length === 0) return;

    this.open = null;

    this.hotspots.forEach((hotspot) => {
      const toggle = hotspot.querySelector('[data-lskf-toggle]');
      const close = hotspot.querySelector('[data-lskf-close]');

      if (toggle) {
        toggle.addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.open === hotspot) this.closeAll();
          else this.show(hotspot);
        });
      }

      if (close) {
        close.addEventListener('click', () => {
          this.closeAll();
          if (toggle) toggle.focus();
        });
      }
    });

    document.addEventListener('click', (event) => {
      if (this.open && !this.open.contains(event.target)) this.closeAll();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.open) return;
      const toggle = this.open.querySelector('[data-lskf-toggle]');
      this.closeAll();
      if (toggle) toggle.focus();
    });

    window.addEventListener('resize', () => {
      if (this.open) this.position(this.open);
    });
  }

  show(hotspot) {
    this.closeAll();

    const popover = hotspot.querySelector('[data-lskf-popover]');
    const toggle = hotspot.querySelector('[data-lskf-toggle]');
    if (!popover) return;

    popover.hidden = false;
    hotspot.classList.add('lskf__hotspot--open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    this.open = hotspot;
    this.position(hotspot);
  }

  /** Nudge the pop-up sideways if it would spill past the edge of the image. */
  position(hotspot) {
    const popover = hotspot.querySelector('[data-lskf-popover]');
    if (!popover || !this.media) return;

    popover.style.setProperty('--lskf-pop-shift', '0px');
    popover.classList.remove('lskf__popover--below');
    if (window.getComputedStyle(popover).transform === 'none') return;

    const bounds = this.media.getBoundingClientRect();
    const margin = 8;

    // Not enough room above the marker (inside the image or the viewport): open below it.
    const above = popover.getBoundingClientRect();
    if (above.top < bounds.top + margin || above.top < margin) {
      popover.classList.add('lskf__popover--below');
    }

    const rect = popover.getBoundingClientRect();
    let shift = 0;

    if (rect.left < bounds.left + margin) shift = bounds.left + margin - rect.left;
    else if (rect.right > bounds.right - margin) shift = bounds.right - margin - rect.right;

    if (shift !== 0) popover.style.setProperty('--lskf-pop-shift', `${Math.round(shift)}px`);
  }

  closeAll() {
    this.hotspots.forEach((hotspot) => {
      const popover = hotspot.querySelector('[data-lskf-popover]');
      const toggle = hotspot.querySelector('[data-lskf-toggle]');

      if (popover) popover.hidden = true;
      hotspot.classList.remove('lskf__hotspot--open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });

    this.open = null;
  }
}

function initLSKeyFeatures(scope) {
  (scope || document)
    .querySelectorAll('[data-section-type="ls-key-features"]')
    .forEach((section) => {
      if (section.dataset.lskfReady === 'true') return;
      section.dataset.lskfReady = 'true';
      new LSKeyFeatures(section);
    });
}

initLSKeyFeatures(document);
document.addEventListener('shopify:section:load', (event) => initLSKeyFeatures(event.target));
